import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook to detect user inactivity and trigger auto-logout
 *
 * @param {number} timeout - Timeout in milliseconds before showing warning (default: 8 hours)
 * @param {number} warningTime - Warning time in milliseconds before auto-logout (default: 5 minutes)
 * @param {function} onWarning - Callback when warning should be shown
 * @param {function} onLogout - Callback when user should be logged out
 * @param {boolean} enabled - Whether the timer is enabled (default: true)
 */
const useInactivityTimer = ({
  timeout = 8 * 60 * 60 * 1000, // 8 hours in milliseconds
  warningTime = 5 * 60 * 1000, // 5 minutes in milliseconds
  onWarning,
  onLogout,
  enabled = true
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Events that count as user activity
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  const clearTimers = () => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startCountdown = () => {
    setTimeRemaining(warningTime);
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1000) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
  };

  const handleWarning = useCallback(() => {
    setShowWarning(true);
    startCountdown();
    if (onWarning) {
      onWarning();
    }

    // Set logout timer for after warning period
    logoutTimerRef.current = setTimeout(() => {
      if (onLogout) {
        onLogout();
      }
    }, warningTime);
  }, [onWarning, onLogout, warningTime]);

  const resetTimer = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    setTimeRemaining(0);

    if (enabled) {
      // Set warning timer
      warningTimerRef.current = setTimeout(handleWarning, timeout);
    }
  }, [enabled, handleWarning, timeout]);

  const handleActivity = useCallback(() => {
    // If warning is showing, don't reset on activity - user must click "Stay Logged In"
    if (!showWarning && enabled) {
      resetTimer();
    }
  }, [showWarning, enabled, resetTimer]);

  const stayLoggedIn = () => {
    setShowWarning(false);
    setTimeRemaining(0);
    resetTimer();
  };

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setShowWarning(false);
      return;
    }

    // Set up activity listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      clearTimers();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeout]);

  return {
    showWarning,
    timeRemaining,
    stayLoggedIn,
    resetTimer
  };
};

export default useInactivityTimer;
